import axios from "axios";
import { dispatchUnauthorizedEvent } from "../shared/authEvents";
import { getToken, clearToken } from "../shared/tokenStorage";

/**
 * Centralized API client with automatic token injection.
 *
 * Token access is delegated to tokenStorage so that every read/write
 * goes through a single module — easier to audit and swap backends.
 *
 * Usage:
 *   import api from '../api/client';
 *
 *   // GET request
 *   const response = await api.get('/users/me');
 *
 *   // POST request
 *   const response = await api.post('/jobs/', data);
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    indexes: null, // Forces axios to omit the `[]` on arrays for correct FastAPI decoding
  },
});

// Automatically add Authorization header to every request
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle common response errors (e.g., token expiration)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - notify app-level auth state
      clearToken();
      dispatchUnauthorizedEvent();
    }
    return Promise.reject(error);
  }
);

export default api;
